import React from "react";
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Tooltip,
    Link,
    Typography
} from "@material-ui/core";
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import FastForwardIcon from '@material-ui/icons/FastForward';
import { AnnualFinalsSeason } from "../../store/annual-finals/models";
import { AnnualFinalsLevelSeasonScore, AnnualFinalsLevelView, isWomenLevel } from "./levelStandings";

interface AnnualFinalsTableProps {
    seasons: AnnualFinalsSeason[];
    level: AnnualFinalsLevelView;
}

const formatPoints = (points: number) => Number.isInteger(points) ? points.toString() : points.toFixed(1).replace(".", ",");

const otherCategoryName = (women: boolean) => women ? "lidze open" : "lidze kobiet";

const seasonTooltip = (score: AnnualFinalsLevelSeasonScore, seasonName: string, women: boolean) => {
    if (!score.played) {
        return score.playedOnlyInOtherCategory
            ? `${seasonName}: zawodniczka grała w tym sezonie tylko w ${otherCategoryName(women)}`
            : `${seasonName}: zawodnik nie grał w tym sezonie`;
    }

    const lines = [
        `Faza zasadnicza: ${score.regularPoints} pkt (do finału ${formatPoints(score.regularPointsCounted)})`,
        `Play-off: ${score.playoffPoints} pkt`,
        `Miejsce: ${score.rank}${!!score.playoffRank ? `, play-off: ${score.playoffRank}` : ""}`,
        `Grupa: ${score.levelName}${score.playedInHigherLevel ? " (grupa wyższa)" : ""}`
    ];

    if (score.otherCategoryLeagues.length > 0) {
        const other = score.otherCategoryLeagues
            .map(l => `${l.levelName} (${formatPoints(l.seasonPoints)} pkt)`)
            .join(", ");

        lines.push(`W tym sezonie zawodniczka grała też w ${otherCategoryName(women)}: ${other} - te punkty liczą się w tamtej klasyfikacji`);
    }

    return `${seasonName} - ${lines.join(", ")}`;
};

const AnnualFinalsTable: React.FC<AnnualFinalsTableProps> = ({ seasons, level }) => {

    const standings = level.standings;
    const women = isWomenLevel(level.id);
    const hasMixedCategoryPlayers = standings.some(standing =>
        standing.seasons.some(score => score.otherCategoryLeagues.length > 0));

    return (
        <>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="right" style={{ width: '50px' }}>#</TableCell>
                            <TableCell>Zawodnik</TableCell>
                            {
                                seasons.map(season => (
                                    <TableCell key={season.no} align="center" style={{ width: '90px' }}>
                                        {
                                            season.hasStarted ? (
                                                <span>{season.name}</span>
                                            ) : (
                                                    <Tooltip title="Sezon jeszcze się nie rozpoczął">
                                                        <span style={{ opacity: 0.5 }}>{season.name}</span>
                                                    </Tooltip>
                                                )
                                        }
                                    </TableCell>
                                ))
                            }
                            <TableCell align="center" style={{ width: '90px' }}>Razem</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            standings.map((standing, i) => {

                                const isLastToAdvance = standing.advances && (i === standings.length - 1 || !standings[i + 1].advances);

                                return (
                                    <TableRow
                                        key={standing.playerId}
                                        style={{
                                            borderBottomColor: isLastToAdvance ? '#ff9100' : undefined,
                                            borderBottomStyle: isLastToAdvance ? 'solid' : undefined,
                                            borderBottomWidth: isLastToAdvance ? '2px' : undefined
                                        }}
                                    >
                                        <TableCell align="right">{standing.seed}.</TableCell>
                                        <TableCell>
                                            <Link href={`/#/stats/${btoa(standing.playerId)}`}>
                                                <span>{standing.name}</span>
                                                <span>&nbsp;</span>
                                                <span>{standing.surname}</span>
                                            </Link>
                                            {
                                                standing.isSeasonWinner ? (
                                                    <Tooltip title={`Zwycięzca sezonu: ${standing.wonSeasons.map(no => seasons.find(s => s.no === no)?.name || no).join(", ")}`}>
                                                        <EmojiEventsIcon color="secondary" fontSize="small" style={{ verticalAlign: 'text-bottom' }} />
                                                    </Tooltip>
                                                ) : (<></>)
                                            }
                                            {
                                                standing.hasBye ? (
                                                    <Tooltip title="Wolny los w 1/8 finału">
                                                        <FastForwardIcon color="disabled" fontSize="small" style={{ verticalAlign: 'text-bottom' }} />
                                                    </Tooltip>
                                                ) : (<></>)
                                            }
                                        </TableCell>
                                        {
                                            standing.seasons.map(score => (
                                                <TableCell key={score.seasonNo} align="center">
                                                    <Tooltip title={seasonTooltip(score, seasons.find(s => s.no === score.seasonNo)?.name || `${score.seasonNo}`, women)}>
                                                        <span>
                                                            {score.played ? formatPoints(score.seasonPoints) : "–"}
                                                            {score.played && score.playedInHigherLevel ? "*" : ""}
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            ))
                                        }
                                        <TableCell align="center"><strong>{formatPoints(standing.totalPoints)}</strong></TableCell>
                                    </TableRow>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography variant="caption" component="div" style={{ marginTop: '8px' }}>
                Punkty sezonu = punkty fazy zasadniczej / 2 + punkty play-off. Zawodnik zbiera punkty w najniższej
                grupie, w jakiej grał w danym roku.
                {
                    hasMixedCategoryPlayers ? (
                        <>
                            &nbsp;Zawodniczki grające jednocześnie w lidze kobiet i w lidze open mają w tej
                            klasyfikacji liczone tylko punkty zdobyte w {women ? "lidze kobiet" : "lidze open"}.
                        </>
                    ) : (<></>)
                }
            </Typography>
            <Typography variant="caption" component="div">
                Pomarańczowa linia - awans do finału rocznego (liczba awansujących: {level.advancingPlayersCount}).
                <EmojiEventsIcon color="secondary" fontSize="small" style={{ verticalAlign: 'text-bottom' }} /> - zwycięzca
                sezonu (automatyczny awans).
                {
                    level.byeSeedsCount > 0 ? (
                        <>
                            <FastForwardIcon color="disabled" fontSize="small" style={{ verticalAlign: 'text-bottom' }} /> - wolny
                            los w 1/8 finału.
                        </>
                    ) : (<></>)
                }
                &nbsp;* - sezon rozegrany w grupie wyższej. – - zawodnik nie grał w tym sezonie w tej kategorii.
            </Typography>
        </>
    );
};

export default AnnualFinalsTable;
